import React from "react";
import { useApp } from "../context/AppContext";
import defaultAvatar from "../assets/avatar.png";

interface InCallScreenProps {
  userId: string;
  conversationId?: string;
  onHangUp: (conversationId?: string) => void;
}

const InCallScreen: React.FC<InCallScreenProps> = ({
  userId,
  conversationId,
  onHangUp,
}) => {
  const { state, toggleMute } = useApp();

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  return (
    <div className="screen in-call-screen">
      <div className="call-info">
        <div className="call-avatar">
          <img src={defaultAvatar} alt="User avatar" />
          <div className="call-status-indicator"></div>
        </div>
        <h2 className="call-user-name">{userId}</h2>
        <p className="call-status">In Call</p>
        <p className="call-duration">{formatTime(state.callDuration)}</p>
      </div>

      <div className="audio-visualizer">
        <div className="wave-container">
          {[...Array(20)].map((_, index) => (
            <div
              key={index}
              className="wave-bar"
              style={{
                animationDelay: `${index * 0.1}s`,
                height: `${Math.random() * 20 + 10}px`,
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="call-controls">
        <button
          className={`control-btn ${state.isAudioMuted ? "active" : ""}`}
          onClick={toggleMute}
          title={state.isAudioMuted ? "Unmute" : "Mute"}
        >
          {state.isAudioMuted ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4C10.3431 4 9 5.34315 9 7V11C9 12.6569 10.3431 14 12 14C13.6569 14 15 12.6569 15 11V7C15 5.34315 13.6569 4 12 4Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M7 10V11C7 13.7614 9.23858 16 12 16C14.7614 16 17 13.7614 17 11V10"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path d="M12 19V16" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M4 4L20 20"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4C10.3431 4 9 5.34315 9 7V11C9 12.6569 10.3431 14 12 14C13.6569 14 15 12.6569 15 11V7C15 5.34315 13.6569 4 12 4Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M7 10V11C7 13.7614 9.23858 16 12 16C14.7614 16 17 13.7614 17 11V10"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path d="M12 19V16" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          )}
        </button>
        <button
          className="control-btn hang-up"
          onClick={() => onHangUp(conversationId)}
          title="End Call"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21.97 18.33C21.97 18.69 21.89 19.06 21.72 19.42C21.55 19.78 21.33 20.12 21.04 20.44C20.55 20.98 20.01 21.37 19.4 21.62C18.8 21.87 18.15 22 17.45 22C16.43 22 15.34 21.76 14.19 21.27C13.04 20.78 11.89 20.12 10.75 19.29C9.6 18.45 8.51 17.52 7.47 16.49C6.44 15.45 5.51 14.36 4.68 13.22C3.86 12.08 3.2 10.94 2.72 9.81C2.24 8.67 2 7.58 2 6.54C2 5.86 2.12 5.21 2.36 4.61C2.6 4 2.98 3.44 3.51 2.94C4.15 2.31 4.85 2 5.59 2C5.87 2 6.15 2.06 6.4 2.18C6.66 2.3 6.89 2.48 7.07 2.74L9.39 6.01C9.57 6.26 9.7 6.49 9.79 6.71C9.88 6.92 9.93 7.13 9.93 7.32C9.93 7.56 9.86 7.8 9.72 8.03C9.59 8.26 9.4 8.5 9.16 8.74L8.4 9.53C8.29 9.64 8.24 9.77 8.24 9.93C8.24 10.01 8.25 10.08 8.27 10.16C8.3 10.24 8.33 10.3 8.35 10.36C8.53 10.69 8.84 11.12 9.28 11.64C9.73 12.16 10.21 12.69 10.73 13.22C11.27 13.75 11.79 14.24 12.32 14.69C12.84 15.13 13.27 15.43 13.61 15.61C13.66 15.63 13.72 15.66 13.79 15.69C13.87 15.72 13.95 15.73 14.04 15.73C14.21 15.73 14.34 15.67 14.45 15.56L15.21 14.81C15.46 14.56 15.7 14.37 15.93 14.25C16.16 14.11 16.39 14.04 16.64 14.04C16.83 14.04 17.03 14.08 17.25 14.17C17.47 14.26 17.7 14.39 17.95 14.56L21.26 16.91C21.52 17.09 21.7 17.31 21.81 17.55C21.91 17.8 21.97 18.05 21.97 18.33Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              transform="rotate(135, 12, 12)"
            />
          </svg>
        </button>
      </div>

      {/* Hidden audio elements */}
      <audio
        id="local-audio"
        autoPlay
        muted
        style={{ display: "none" }}
      ></audio>
      <audio id="remote-audio" autoPlay style={{ display: "none" }}></audio>
    </div>
  );
};

export default InCallScreen;
